import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import type { AuditRow, DataCoverage, EmployeeRef } from './types';

/* ---------------------------------------------------------------------------
   Cross-cutting patterns from the brief, shared by every Cyntox site. Each of
   these exists once and is reused everywhere the situation arises, so the
   behaviour cannot drift between screens or between apps.
   --------------------------------------------------------------------------- */

/**
 * How far the imported data reaches, on every screen of the scorecard and Admin.
 *
 * A period runs to the end of its quarter whether or not anything has been
 * imported for it, and the date filters default to the period — so a week with
 * no punch data looks exactly like a week with nothing to report. This is the
 * one line that tells them apart, and it belongs everywhere rather than on the
 * dashboard alone: the person reviewing flags is the one most likely to be
 * misled by it, and the person loading files needs to know what has landed.
 *
 * Takes the coverage as a prop rather than reading context, so it stays a plain
 * presentational component and can be unit-checked without a provider.
 */
const IMPORT_LABEL: Record<string, string> = {
  hr: 'punch data',
  employees: 'employees',
  schedules: 'schedules',
};

export function DataCoverageBar({ coverage }: { coverage?: DataCoverage }) {
  if (!coverage) return null;

  const { lastPunchDay, lastDay, periodName, periodEnd, lastImports } = coverage;

  // Each kind, named. One undifferentiated "last import" date claimed on the
  // Employees screen that employees had been loaded when only punches had.
  const imports = (
    <span className="coverage__meta">
      last loaded ·{' '}
      {lastImports.map((m, i) => (
        <span key={m.type} title={m.by ? `by ${m.by}` : undefined}>
          {i > 0 && ' · '}
          {IMPORT_LABEL[m.type] ?? m.type}{' '}
          <b>{m.atUtc ? formatDate(m.atUtc) : 'never'}</b>
        </span>
      ))}
    </span>
  );

  // Nothing imported for this period at all — the case most likely to be read
  // as "a clean board" when it means "no data yet".
  if (!lastDay) {
    return (
      <div className="coverage coverage--empty">
        <span className="coverage__dot" aria-hidden="true" />
        <span><b>No punch data imported for {periodName} yet.</b> Scores stay at 100 until it is loaded and reviewed.</span>
        {imports}
      </div>
    );
  }

  const reach = lastPunchDay ?? lastDay;
  const daysBehind = Math.round((parseDate(today()).getTime() - parseDate(reach).getTime()) / 86_400_000);
  // Two days covers a normal weekday lag and a weekend; past that, the gap is
  // worth pointing at rather than merely stating.
  const stale = daysBehind > 2;
  const shortOfPeriod = parseDate(reach) < parseDate(periodEnd);

  return (
    <div className={`coverage${stale ? ' coverage--stale' : ''}`}>
      <span className="coverage__dot" aria-hidden="true" />
      <span>
        {/* Named, because this bar shows on screens that are not about punches
            at all and "data" there reads as whatever that screen holds. */}
        Punch data through <b>{formatLongDate(reach)}</b>
        {daysBehind > 0 && <> · {daysBehind} day{daysBehind === 1 ? '' : 's'} ago</>}
        {shortOfPeriod && <> · {periodName} runs to {formatLongDate(periodEnd)}, so anything after {formatDate(reach)} has no data yet</>}
      </span>
      {imports}
    </div>
  );
}

/**
 * Row-level conflict. A second save on an already-changed record is blocked
 * and the user is told to reload — never a silent overwrite.
 */
export function ConflictBanner({ message, onReload }: { message: string; onReload: () => void }) {
  return (
    <div className="banner banner--conflict" role="alert">
      <div>
        <strong>This record was changed by someone else.</strong>{' '}
        {message || 'Reload to see the latest version before acting on it.'}
      </div>
      <button className="btn btn--sm" style={{ background: 'var(--orange)', color: '#fff', border: 'none' }} onClick={onReload}>
        Reload record
      </button>
    </div>
  );
}

export function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div className="banner banner--error" role="alert">
      <div>{message}</div>
      {onDismiss && <button className="btn btn--sm" onClick={onDismiss}>Dismiss</button>}
    </div>
  );
}

/**
 * A locked period is read-only forever. Nothing is overwritten by the next
 * period — it is only superseded — so the wording says "locked", not "closed".
 */
export function LockedBanner({ periodName }: { periodName: string }) {
  return (
    <div className="banner banner--locked">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 14, height: 14, border: '2px solid var(--muted)', borderRadius: 3, flex: 'none' }} />
        <span>
          <strong>{periodName}</strong> is locked. Records dated inside it stay viewable but can no longer be edited.
        </span>
      </div>
    </div>
  );
}

export function GraceBanner({ periodName, days }: { periodName: string; days: number }) {
  return (
    <div className="banner banner--info">
      <div>
        <strong>{periodName} is in its grace window</strong> ({days} days). Only roles allowed to edit during grace
        days can still change records dated inside it; everyone else can view them.
      </div>
    </div>
  );
}

/**
 * An employee id with no row in the HR master. Show the id and say why the
 * name is missing — never a blank cell, and never a broken row.
 */
export function EmployeeName({ employee, subtext }: { employee: EmployeeRef; subtext?: string | null }) {
  if (!employee.resolved) {
    return (
      <div>
        <span className="unresolved">Employee {employee.employeeId}</span>
        <div className="unresolved__note">not in employee master</div>
      </div>
    );
  }
  return (
    <div>
      <span>{employee.name}</span>
      {subtext && <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)' }}>{subtext}</div>}
    </div>
  );
}

export function StatusPill({ label, color, bg }: { label: string; color: string; bg: string }) {
  return <span className="pill" style={{ background: bg, color }}>{label}</span>;
}

export function Points({ value }: { value: number }) {
  const color = value < 0 ? 'var(--red)' : value > 0 ? 'var(--green)' : 'var(--muted)';
  const label = value === 0 ? '—' : value > 0 ? `+${trim(value)}` : trim(value);
  return <span style={{ fontWeight: 700, color }}>{label}</span>;
}

/**
 * Audit "view history": one expandable inline trail, identical for every
 * kind of record, read from the one audit log.
 */
export function HistoryPanel({ rows, loading }: { rows?: AuditRow[]; loading: boolean }) {
  return (
    <>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.3, marginBottom: 6 }}>
        HISTORY
      </div>
      {loading && <div className="meta">Loading…</div>}
      {!loading && (!rows || rows.length === 0) && (
        <div className="meta">No changes recorded yet — this record is exactly as it was generated.</div>
      )}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
        {rows?.map((r) => (
          <div key={r.auditId}>
            {formatDateTime(r.changedAtUtc)} — {r.summary ?? `${r.action} by ${r.changedBy}`}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * Location filter. Offers only the facilities the person's access reaches on
 * this screen. "All locations" appears only when that really is all of them —
 * offering it to a two-plant manager would promise a breadth they do not
 * have — and the control locks when there is exactly one.
 */
export function LocationSelect({
  facilities, allowed, value, onChange,
}: {
  facilities: { code: string; name: string }[];
  /** Codes this screen's capability reaches. null or omitted = every location. */
  allowed?: string[] | null;
  value: string;
  onChange: (code: string) => void;
}) {
  const everywhere = allowed == null;
  const reachable = everywhere ? facilities : facilities.filter((f) => (allowed ?? []).includes(f.code));
  const only = allowed && allowed.length === 1 ? allowed[0] : null;
  return (
    <select
      className="control"
      value={only ?? value}
      disabled={!!only}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Location"
      title={only ? 'Your access is scoped to this location' : undefined}
    >
      {!only && <option value="all">{everywhere ? 'All locations' : 'All my locations'}</option>}
      {/* Employees whose export carried no location import without error and
          then vanish from every location view. Filterable so they can be found,
          by people whose access covers every location. */}
      {everywhere && <option value="__none__">— No location set —</option>}
      {reachable.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
    </select>
  );
}

/** CSV / Excel / PDF over the current filtered and sorted view, not the whole table. */
export function ExportMenu({ onExport }: { onExport: (format: 'csv' | 'excel' | 'pdf') => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className="btn" onClick={() => setOpen((v) => !v)}>Export ▾</button>
      {open && (
        <div className="export-menu">
          {(['csv', 'excel', 'pdf'] as const).map((f) => (
            <button key={f} onClick={() => { setOpen(false); onExport(f); }}>
              {f === 'csv' ? 'Download CSV' : f === 'excel' ? 'Download Excel' : 'Download PDF'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A metric card that navigates carrying its filter must show that filter as a
 * removable chip on arrival — not a silent pre-filter, or the short list looks
 * like missing data.
 */
export function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button className="chip" onClick={onClear}>
      {label} <span style={{ fontSize: 12 }}>✕</span>
    </button>
  );
}

/* ---------------------------------------------------------------------------
   Sortable tables

   Every table in every Cyntox app sorts on every column that holds data. A new
   table gets that by naming how to read each column, then using SortableHeader
   for those headings:

     const { rows, sort, onSort } = useTableSort(data, {
       name: (r) => r.name,
       hours: (r) => r.workedMinutes,
     });
     <SortableHeader label="NAME" column="name" sort={sort} onSort={onSort} />

   Leave a header plain only where sorting would get in the way, such as a column
   of inputs someone is typing into, whose rows would move under them.
   --------------------------------------------------------------------------- */

export type SortDir = 'asc' | 'desc';
export interface SortState { column: string; dir: SortDir }
export type SortValue = string | number | boolean | null | undefined;

const isBlank = (v: SortValue) => v == null || v === '';

/**
 * Sorts rows by the clicked column. Text sorts naturally, so "Truck 9" comes
 * before "Truck 10"; blanks go last in either direction, so an empty cell never
 * pushes the real values off the top; ties keep the order the rows came in.
 *
 * With no initial sort the rows stay in the order given — a screen's own
 * meaningful order, such as newest first — until a heading is clicked. The first
 * click on a column sorts it ascending, the next descending.
 */
export function useTableSort<T>(
  rows: readonly T[],
  columns: Record<string, (row: T) => SortValue>,
  initial: SortState | null = null,
) {
  const [sort, setSort] = useState<SortState | null>(initial);

  const sorted = useMemo(() => {
    const read = sort ? columns[sort.column] : undefined;
    if (!sort || !read) return [...rows];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return rows
      .map((row, index) => ({ row, index, value: read(row) }))
      .sort((a, b) => {
        const blankA = isBlank(a.value);
        const blankB = isBlank(b.value);
        if (blankA !== blankB) return blankA ? 1 : -1;
        const order = blankA ? 0 : compareSortValues(a.value, b.value) * dir;
        return order || a.index - b.index;
      })
      .map((x) => x.row);
  }, [rows, columns, sort]);

  const onSort = (column: string) =>
    setSort((s) => ({ column, dir: s?.column === column && s.dir === 'asc' ? 'desc' : 'asc' }));

  return { rows: sorted, sort, onSort };
}

function compareSortValues(a: SortValue, b: SortValue): number {
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }
  return Number(a) - Number(b);
}

export function SortableHeader({
  label, column, sort, onSort, className, style, title,
}: {
  label: string;
  column: string;
  sort: SortState | null;
  onSort: (column: string) => void;
  className?: string;
  style?: CSSProperties;
  title?: string;
}) {
  const dir = sort && sort.column === column ? sort.dir : null;
  return (
    <th
      className={`sortable ${className ?? ''}`}
      style={style}
      title={title}
      tabIndex={0}
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
      onClick={() => onSort(column)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(column); }
      }}
    >
      {label}{dir === 'asc' ? ' ↑' : dir === 'desc' ? ' ↓' : ''}
    </th>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="spinner-note">{children}</td>
    </tr>
  );
}

/* ---------------------------------------------------------------------------
   Formatting
   --------------------------------------------------------------------------- */

export function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/** Dates come back as plain yyyy-MM-dd; parse them as local so the day never shifts. */
export function parseDate(iso: string): Date {
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function formatDate(iso: string): string {
  return parseDate(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatLongDate(iso: string): string {
  return parseDate(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A person as the employee picker shows them. */
export interface PickablePerson {
  employeeId: number;
  displayName: string;
  department?: string | null;
  locationCode?: string | null;
}

/** How a person reads once picked: "Tonice Bridgefort (15)". */
export function personLabel(p: PickablePerson): string {
  return `${p.displayName} (${p.employeeId})`;
}

/**
 * Search-as-you-type employee picker. The app draws it rather than using the
 * browser's <datalist>, which cannot be styled, spaces its rows for touch, and
 * gives no sign that more matches exist. This lists every match in a box that
 * scrolls, sized so the last visible row is cut in half and plainly continues,
 * and always says how many there are. Scroll to browse; type to narrow.
 *
 * Resolves to one person on a picked suggestion, a bare ID, or a full name
 * that belongs to exactly one person. Anything else is a search in progress
 * and reports null, with the typed text as the second argument so a caller can
 * tell "cleared" from "still searching". Opens showing whoever `value` already
 * is. To clear it from outside, change its key.
 */
export function EmployeePicker({
  id, people, value, onChange, placeholder = 'Search by name or ID…',
}: {
  id: string;
  people: PickablePerson[];
  value: number | null;
  onChange: (employeeId: number | null, text: string) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // Set once the person types, so a roster that loads late never overwrites them.
  const typed = useRef(false);

  // Someone already linked reads as that person, not as an empty box that
  // looks unlinked. The roster often arrives after the first render.
  useEffect(() => {
    if (typed.current || value == null) return;
    const person = people.find((p) => p.employeeId === value);
    if (person) setQuery(personLabel(person));
  }, [people, value]);
  // Set while the mouse is down inside the list. Grabbing its scrollbar takes
  // focus off the input, and that blur must not close the list.
  const pressingList = useRef(false);
  const listId = `${id}-list`;

  const matches = useMemo(() => rankPeople(people, query), [people, query]);

  // Keep the highlighted row in view as the arrow keys move past the box's edge.
  useEffect(() => {
    if (!open || !matches[active]) return;
    document.getElementById(`${listId}-${matches[active].employeeId}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, matches, listId]);

  // Releasing the mouse anywhere ends a press that began in the list, and
  // hands focus back to the input so typing carries on.
  useEffect(() => {
    const release = () => {
      if (!pressingList.current) return;
      pressingList.current = false;
      inputRef.current?.focus();
    };
    window.addEventListener('mouseup', release);
    return () => window.removeEventListener('mouseup', release);
  }, []);

  const choose = (p: PickablePerson) => {
    pressingList.current = false;
    typed.current = true;
    setQuery(personLabel(p));
    setOpen(false);
    onChange(p.employeeId, personLabel(p));
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (matches.length === 0 ? 0 : (i + step + matches.length) % matches.length));
    } else if (e.key === 'Enter' && open && matches[active]) {
      // Enter picks the highlighted person rather than submitting anything.
      e.preventDefault();
      choose(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const unresolved = !open && query.trim() !== '' && value === null;

  return (
    <div className="picker">
      <input
        ref={inputRef}
        id={id}
        className="control"
        style={{ width: '100%' }}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? `${listId}-${matches[active].employeeId}` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          typed.current = true;
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
          onChange(resolvePerson(people, e.target.value)?.employeeId ?? null, e.target.value);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (!pressingList.current) setOpen(false); }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div className="picker__list" onMouseDown={() => { pressingList.current = true; }}>
          {matches.length > 0 && (
            <ul className="picker__scroll" id={listId} role="listbox">
              {matches.map((p, i) => (
                <li
                  key={p.employeeId}
                  id={`${listId}-${p.employeeId}`}
                  role="option"
                  aria-selected={i === active}
                  className={`picker__option${i === active ? ' picker__option--active' : ''}`}
                  // mousedown, not click: picking has to beat the input's blur.
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); choose(p); }}
                  onMouseEnter={() => setActive(i)}
                >
                  <div className="picker__name">
                    <Highlight text={p.displayName} term={query} /> <span className="picker__id">({p.employeeId})</span>
                  </div>
                  {(p.locationCode || p.department) && (
                    <div className="picker__sub">{[p.locationCode, p.department].filter(Boolean).join(' · ')}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="picker__footer">{footerText(matches.length, query)}</div>
        </div>
      )}
      {unresolved && <div className="meta" style={{ marginTop: 4 }}>No match yet. Choose someone from the list.</div>}
    </div>
  );
}

/** Rows the list shows before it scrolls; keep in step with .picker__scroll. */
const VISIBLE_ROWS = 7;

function footerText(count: number, query: string): string {
  if (count === 0) return `No one matches “${query.trim()}”.`;
  if (query.trim() === '') return `${count} people. Scroll or type to narrow.`;
  const found = `${count} ${count === 1 ? 'match' : 'matches'}`;
  return count > VISIBLE_ROWS ? `${found}. Scroll or keep typing to narrow.` : found;
}

/** A picked label searches by its name part, so reopening the list still finds the person. */
function searchTerm(text: string): string {
  return text.trim().toLowerCase().replace(/\s*\(\d+\)$/, '');
}

/**
 * Best matches first: an exact ID or a name that starts with the term, then
 * any word or ID that starts with it, then a name that merely contains it.
 * Ties keep the order the roster arrived in (by surname).
 */
function rankPeople(people: PickablePerson[], text: string): PickablePerson[] {
  const t = searchTerm(text);
  if (!t) return people;
  const rank = (p: PickablePerson) => {
    const name = p.displayName.toLowerCase();
    const id = String(p.employeeId);
    if (id === t || name.startsWith(t)) return 0;
    if (id.startsWith(t) || name.split(/\s+/).some((w) => w.startsWith(t))) return 1;
    if (name.includes(t)) return 2;
    return -1;
  };
  return people
    .map((p, index) => ({ p, index, r: rank(p) }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.index - b.index)
    .map((x) => x.p);
}

function resolvePerson(people: PickablePerson[], text: string): PickablePerson | undefined {
  const t = text.trim().toLowerCase();
  if (!t) return undefined;
  const exact = people.find((p) => personLabel(p).toLowerCase() === t || String(p.employeeId) === t);
  if (exact) return exact;
  const byName = people.filter((p) => p.displayName.toLowerCase() === t);
  return byName.length === 1 ? byName[0] : undefined;
}

function Highlight({ text, term }: { text: string; term: string }) {
  const t = searchTerm(term);
  const i = t ? text.toLowerCase().indexOf(t) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + t.length)}</mark>{text.slice(i + t.length)}</>;
}

/** Downloads the rows the user is actually looking at. */
export function downloadCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]) {
  const escape = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\r\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
